import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { listFailedLogins, type FailedLogin } from '../lib/api';
import { WafDataGrid } from '../components/WafDataGrid';

function useDebounced<T>(value: T, ms = 150): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// Above this many failures for one email (within the loaded window) we flag it
// as a likely targeted/brute-force attempt rather than a fat-fingered password.
const REPEAT_FLAG_THRESHOLD = 5;

export function FailedLogins() {
  const [rows, setRows] = useState<FailedLogin[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query, 150);

  useEffect(() => {
    let cancelled = false;
    listFailedLogins()
      .then((data) => {
        if (cancelled) return;
        setRows(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // How many times each email appears in the loaded window — drives the
  // "attempts" column and the repeat-offender highlight.
  const attemptsByEmail = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) m.set(r.email, (m.get(r.email) ?? 0) + 1);
    return m;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.email.toLowerCase().includes(q) || r.audience.toLowerCase().includes(q));
  }, [rows, debouncedQuery]);

  const columns: GridColDef<FailedLogin>[] = [
    {
      field: 'occurred_at',
      headerName: 'When',
      width: 190,
      valueGetter: (_v, row) => new Date(row.occurred_at),
      renderCell: (p: GridRenderCellParams<FailedLogin>) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(p.row.occurred_at).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'email',
      headerName: 'Attempted email',
      flex: 1,
      minWidth: 260,
      renderCell: (p: GridRenderCellParams<FailedLogin>) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {p.row.email || '(blank)'}
        </Typography>
      ),
    },
    {
      field: 'audience',
      headerName: 'Portal',
      width: 130,
      renderCell: (p: GridRenderCellParams<FailedLogin>) => (
        <Chip
          size="small"
          variant="outlined"
          label={p.row.audience === 'entrant' ? 'Architect' : 'Staff'}
        />
      ),
    },
    {
      field: 'attempts',
      headerName: 'Attempts (email)',
      width: 170,
      sortable: false,
      valueGetter: (_v, row) => attemptsByEmail.get(row.email) ?? 1,
      renderCell: (p: GridRenderCellParams<FailedLogin>) => {
        const n = attemptsByEmail.get(p.row.email) ?? 1;
        const flagged = n >= REPEAT_FLAG_THRESHOLD;
        return (
          <Chip
            size="small"
            color={flagged ? 'error' : 'default'}
            variant={flagged ? 'filled' : 'outlined'}
            label={`${n}×`}
          />
        );
      },
    },
  ];

  const gridRows = filtered.map((r) => ({ ...r }));

  return (
    <Stack spacing={3}>
      <Typography variant="h3" component="h1" align="center">
        Failed logins
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          size="small"
          placeholder="Search email or portal…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flex: 1, width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: '100%', sm: 520 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
        <Typography variant="body2" color="text.secondary">
          {filtered.length} of {rows.length} (last 30 days)
        </Typography>
      </Stack>

      <Paper>
        <WafDataGrid<FailedLogin>
          rows={gridRows}
          columns={columns}
          loading={loading}
          density="standard"
          pageSizeOptions={[25, 50, 100, 250]}
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
            sorting: { sortModel: [{ field: 'occurred_at', sort: 'desc' }] },
          }}
        />
      </Paper>
    </Stack>
  );
}
